import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Form } from 'react-final-form'
import { Row, Col, Button, Spin, Upload, Image, Typography, Divider, message, Modal } from 'antd'
import { Field } from 'react-final-form'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import TextInput from 'components/TextInput'
import TextArea from 'components/TextArea/TextArea'
import Select from 'components/Select'
import DatePicker from 'components/DatePicker/DatePicker'
import countries from 'const/country'
import useAction from 'utils/useAction'
import { required, validLink, isValidLink } from 'helpers/validators'
import { selectors as arcAdsSelectors } from 'domains/arcAds'
import { selectors as arcAdvertisersSelectors } from 'domains/arcAdvertisers'
import * as arcAdsActions from 'actions/views/arcAds'
import * as arcAdvertisersActions from 'actions/views/arcAdvertisers'
import { formatArcAdForApi, formatArcAdForForm, STATUS_OPTIONS } from 'domains/arcAds/formatters'
import { formatArcAdvertiserForTable } from 'domains/arcAdvertisers/formatters'
import { StyledForm, FormSection, ButtonContainer, ImageUploadSection, ImagePreview } from './AdForm.styles'

const { Title, Text } = Typography

// Simple validators - add these after the Typography import
const validateRequired = (value) => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return 'This field is required'
  }
  return undefined
}

const validateOptionalURL = (value) => {
  if (!value || value.trim() === '') return undefined
  const urlPattern = /^https?:\/\/.+\..+/
  if (!urlPattern.test(value)) {
    return 'Please enter a valid URL'
  }
  return undefined
}

const validateRequiredURL = (value) => {
  const requiredError = validateRequired(value)
  if (requiredError) return requiredError
  return validateOptionalURL(value)
}

// Countries options for select
const countryOptions = countries.map(country => ({
  value: country.code,
  label: `${country.code} - ${country.name}`
}))

const AdForm = ({ adId, onSuccess, onCancel }) => {
  const [iconFile, setIconFile] = useState(null)
  const [heroFile, setHeroFile] = useState(null)
  const [iconPreview, setIconPreview] = useState(null)
  const [heroPreview, setHeroPreview] = useState(null)
  const [isUploadingIcon, setIsUploadingIcon] = useState(false)
  const [isUploadingHero, setIsUploadingHero] = useState(false)

  const isSaving = useSelector(arcAdsSelectors.getIsSavingActive)
  const selectedAd = useSelector(arcAdsSelectors.getSelectedAd)
  const getAdById = useSelector(arcAdsSelectors.getArcAdById)
  const includedData = useSelector(arcAdsSelectors.getIncludedData)
  const sources = useSelector(arcAdsSelectors.getSources)
  const isSourcesLoaded = useSelector(arcAdsSelectors.getIsSourcesLoaded)
  const advertisers = useSelector(arcAdvertisersSelectors.getArcAdvertisers)
  const isAdvertisersLoaded = useSelector(arcAdvertisersSelectors.getIsLoaded)
  const createAd = useAction(arcAdsActions.createArcAd)
  const updateAd = useAction(arcAdsActions.updateArcAd)
  const fetchAdDetails = useAction(arcAdsActions.fetchArcAdDetails)
  const fetchAdvertisers = useAction(arcAdvertisersActions.fetchArcAdvertisers)
  const fetchSources = useAction(arcAdsActions.fetchArcAdSources)

  const ad = adId ? getAdById(adId) || selectedAd : null
  const isEdit = !!adId

  useEffect(() => {
    if (!isAdvertisersLoaded) {
      fetchAdvertisers({ page: 1, search: '', filters: {}, sorter: {} })
    }
  }, [isAdvertisersLoaded, fetchAdvertisers])

  useEffect(() => {
    if (!isSourcesLoaded) {
      fetchSources()
    }
  }, [isSourcesLoaded, fetchSources])

  useEffect(() => {
    if (adId && !ad) {
      fetchAdDetails({ id: adId })
    }
  }, [adId, ad, fetchAdDetails])

  // Set initial image previews when ad data is loaded
  useEffect(() => {
    if (isEdit && ad && ad.attributes) {
      if (ad.attributes.icon_url && !iconPreview) {
        setIconPreview(ad.attributes.icon_url)
      }
      if (ad.attributes.hero_url && !heroPreview) {
        setHeroPreview(ad.attributes.hero_url)
      }
    }
  }, [isEdit, ad, iconPreview, heroPreview])

  const convertFileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }, [])

  const handleIconUpload = useCallback(async (file) => {
    try {
      const maxSize = 2 * 1024 * 1024
      if (file.size > maxSize) {
        message.error('File size must be less than 2MB')
        return false
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        message.error('Please upload a valid image file (JPEG, PNG, or GIF)')
        return false
      }
      setIsUploadingIcon(true)
      const base64 = await convertFileToBase64(file)
      setIconFile(file)
      setIconPreview(base64)
      message.success('Icon uploaded successfully')
      return false // Prevent auto upload
    } catch (error) {
      console.error('Error converting icon to base64:', error)
      message.error('Failed to upload icon. Please try again.')
      return false
    } finally {
      setIsUploadingIcon(false)
    }
  }, [convertFileToBase64])

  const handleHeroUpload = useCallback(async (file) => {
    try {
      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024 // 2MB
      if (file.size > maxSize) {
        message.error('File size must be less than 2MB')
        return false
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        message.error('Please upload a valid image file (JPEG, PNG, or GIF)')
        return false
      }

      setIsUploadingHero(true)
      const base64 = await convertFileToBase64(file)
      setHeroFile(file)
      setHeroPreview(base64)
      message.success('Hero image uploaded successfully')
      return false // Prevent auto upload
    } catch (error) {
      console.error('Error converting hero to base64:', error)
      message.error('Failed to upload hero image. Please try again.')
      return false
    } finally {
      setIsUploadingHero(false)
    }
  }, [convertFileToBase64])

  const handleRemoveIcon = () => {
    Modal.confirm({
      title: 'Remove Icon',
      content: 'Are you sure you want to remove the current icon? This action cannot be undone.',
      okText: 'Yes, Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk() {
        setIconFile(null)
        setIconPreview(null)
        const formattedData = {
          type: 'arc_ads',
          attributes: {clear_icon_image: true },
          id: adId
        }
        updateAd({
          data: formattedData,
          callback: () => {
            onSuccess()
          }
        })
        message.success('Icon removed successfully')
      },
    })
  }

  const handleRemoveHero = () => {
    Modal.confirm({
      title: 'Remove Hero Image',
      content: 'Are you sure you want to remove the current hero image? This action cannot be undone.',
      okText: 'Yes, Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk() {
        setHeroFile(null)
        setHeroPreview(null)
        const formattedData = {
            type: 'arc_ads',
            attributes: {clear_hero_image: true },
            id: adId
        }
        updateAd({
          data: formattedData,
          callback: () => {
            onSuccess()
          }
        })
        message.success('Hero image removed successfully')
      },
    })
  }

  const handleSubmit = async (values) => {
    try {
      const formattedData = formatArcAdForApi(values)

      // Add base64 image data if new files were uploaded
      if (iconPreview && iconFile) {
        formattedData.attributes.icon_url_base64 = iconPreview
        if (!formattedData.attributes.icon_width) {
          formattedData.attributes.icon_width = 150
        }
        if (!formattedData.attributes.icon_height) {
          formattedData.attributes.icon_height = 150
        }
      }

      if (heroPreview && heroFile) {
        formattedData.attributes.hero_url_base64 = heroPreview
        if (!formattedData.attributes.hero_width) {
          formattedData.attributes.hero_width = 600
        }
        if (!formattedData.attributes.hero_height) {
          formattedData.attributes.hero_height = 400
        }
      }

      if (isEdit) {
        formattedData.id = adId
        updateAd({
          data: formattedData,
          callback: () => {
            onSuccess()
          }
        })
      } else {
        createAd({
          data: formattedData,
          callback: () => {
            onSuccess()
          }
        })
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      message.error('Failed to save ad. Please try again.')
    }
  }

  const advertiserOptions = (advertisers || []).map(advertiser => {
    const formatted = formatArcAdvertiserForTable(advertiser)
    return {
      value: formatted.id,
      label: formatted.name
    }
  })

  const sourceOptions = (sources || []).map(source => ({
    value: source.id,
    label: source.name
  }))

  const initialValues = isEdit && ad ? formatArcAdForForm(ad, includedData) : {}

  if (isEdit && !ad) {
    return (
      <Spin spinning={true}>
        <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading ad details...
        </div>
      </Spin>
    )
  }

  // Show loading state if advertisers or sources are not loaded yet
  if (!isAdvertisersLoaded || !isSourcesLoaded) {
    return (
      <Spin spinning={true}>
        <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading form data...
        </div>
      </Spin>
    )
  }

  return (
    <Spin spinning={isSaving}>
      <Form
        key={isEdit ? `${adId}-${ad?.id}-${includedData?.length || 0}` : 'create'} // Force re-render when ad data or included data changes
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={(values) => {
          const errors = {}
          // Manual validation to ensure required fields are checked
          if (!values.header) {
            errors.header = 'Header is required'
          }
          if (!values.click_url) {
            errors.click_url = 'Click URL is required'
          }
          if (!values.source_id) {
            errors.source_id = 'Source is required'
          }
          return errors
        }}
        render={({ handleSubmit, pristine, hasValidationErrors, values }) => {
          return (
            <StyledForm onSubmit={handleSubmit}>
              <FormSection>
                <Title level={4}>Basic Information</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Banner ID
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Unique identifier for the banner campaign
                    </Text>
                    <Field
                      name="banner_id"
                      label="Banner ID"
                      placeholder="Enter banner ID"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Campaign ID
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Associated campaign identifier
                    </Text>
                    <Field
                      name="campaign_id"
                      label="Campaign ID"
                      placeholder="Enter campaign ID"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Header *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Main title that will be displayed prominently in the ad
                    </Text>
                    <Field
                      required
                      name="header"
                      validate={validateRequired}
                      label="Header"
                      placeholder="Enter ad header"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Short Description
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Brief summary or tagline for the ad
                    </Text>
                    <Field
                      name="short_description"
                      label="Short Description"
                      placeholder="Enter short description"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Call-to-Action Text
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Button text that encourages user interaction
                    </Text>
                    <Field
                      name="cta_text"
                      label="CTA Text"
                      placeholder="e.g., Shop Now, Learn More"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Full Description
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Detailed description of the product or service being advertised
                    </Text>
                    <Field
                      name="description"
                      label="Description"
                      placeholder="Enter full description"
                      rows={3}
                      component={TextArea}
                    />
                  </Col>
                </Row>
              </FormSection>
              <Divider />
              <FormSection>
                <Title level={4}>Status & Schedule</Title>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Status
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Current status of the ad campaign
                    </Text>
                    <Field
                      name="status"
                      label="Status"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      component={Select}
                    />
                  </Col>
                  <Col span={8}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Start Date
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      When the ad campaign should start
                    </Text>
                    <Field
                      name="started_at"
                      label="Started At"
                      placeholder="Select start date"
                      component={DatePicker}
                    />
                  </Col>
                  <Col span={8}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      End Date
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      When the ad campaign should end
                    </Text>
                    <Field
                      name="end_at"
                      label="End At"
                      placeholder="Select end date"
                      component={DatePicker}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>URLs</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Terms & Conditions URL
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Link to terms and conditions page (optional)
                    </Text>
                    <Field
                      name="terms_url"
                      label="Terms URL"
                      validate={validateOptionalURL}
                      placeholder="https://example.com/terms"
                      component={TextInput}
                    />
                  </Col>

                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Analytics Beacon URL
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Tracking URL for analytics and monitoring (optional)
                    </Text>
                    <Field
                      name="beacon_url"
                      label="Beacon URL"
                      validate={validateOptionalURL}
                      placeholder="https://analytics.com/beacon"
                      component={TextInput}
                    />
                  </Col>

                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Click Destination URL *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Primary destination where users will be redirected when clicking the ad
                    </Text>
                    <Field
                      required
                      name="click_url"
                      validate={validateRequiredURL}
                      label="Click URL"
                      placeholder="https://example.com/product"
                      component={TextInput}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Footer Information</Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Footer Text
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Additional text to display at the bottom of the ad
                    </Text>
                    <Field
                      name="footer_text"
                      label="Footer Text"
                      placeholder="Enter footer text"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Footer Policy Text
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Policy-related text for the footer
                    </Text>
                    <Field
                      name="footer_policy_text"
                      label="Footer Policy Text"
                      placeholder="Enter footer policy text"
                      component={TextInput}
                    />
                  </Col>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Footer Policy URL
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Link to policy page referenced in footer
                    </Text>
                    <Field
                      name="footer_policy_url"
                      label="Footer Policy URL"
                      validate={validateOptionalURL}
                      placeholder="https://example.com/policy"
                      component={TextInput}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Targeting</Title>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Target Countries
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Select the countries where this ad should be displayed
                    </Text>
                    <Field
                      name="countries"
                      label="Countries"
                      options={countryOptions}
                      placeholder="Select target countries"
                      mode="multiple"
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        if (!input) return true
                        const label = option?.children || option?.props?.children
                        if (!label) return true
                        return label.toLowerCase().includes(input.toLowerCase())
                      }}
                      component={Select}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Advertiser</Title>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Associated Advertiser
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Select the company or brand this ad represents
                    </Text>
                    <Field
                      name="arc_advertiser_id"
                      label="Select Advertiser"
                      options={advertiserOptions}
                      placeholder="Choose an advertiser"
                      allowClear
                      component={Select}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Source</Title>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Source *
                    </Text>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Select the allowed source for this ad campaign
                    </Text>
                    <Field
                      required
                      name="source_id"
                      validate={validateRequired}
                      label="Source"
                      options={sourceOptions}
                      placeholder="Select a source"
                      allowClear
                      component={Select}
                    />
                  </Col>
                </Row>
              </FormSection>

              <Divider />

              <FormSection>
                <Title level={4}>Images</Title>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <ImageUploadSection>
                      <Title level={5}>Icon</Title>
                      {iconPreview ? (
                        <ImagePreview>
                          <Image
                            width={150}
                            height={150}
                            src={iconPreview}
                            alt="Icon preview"
                            style={{ borderRadius: 8 }}
                          />
                          <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveIcon}
                            style={{ marginTop: 8 }}
                            disabled={isUploadingIcon}
                          >
                            Remove
                          </Button>
                        </ImagePreview>
                      ) : (
                        <Upload
                          beforeUpload={handleIconUpload}
                          accept="image/*"
                          showUploadList={false}
                          disabled={isUploadingIcon}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={isUploadingIcon}
                            disabled={isUploadingIcon}
                          >
                            {isUploadingIcon ? 'Uploading...' : 'Upload Icon'}
                          </Button>
                        </Upload>
                      )}
                      {/* Icon dimensions */}
                      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Icon Width
                          </Text>
                          <Field
                            name="icon_width"
                            label="Icon Width"
                            placeholder="150"
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Icon Height
                          </Text>
                          <Field
                            name="icon_height"
                            label="Icon Height"
                            placeholder="150"
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                      </Row>
                    </ImageUploadSection>
                  </Col>
                  <Col span={12}>
                    <ImageUploadSection>
                      <Title level={5}>Hero Image</Title>
                      {heroPreview ? (
                        <ImagePreview>
                          <Image
                            width={200}
                            height={133}
                            src={heroPreview}
                            alt="Hero preview"
                            style={{ borderRadius: 8 }}
                          />
                          <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleRemoveHero}
                            style={{ marginTop: 8 }}
                            disabled={isUploadingHero}
                          >
                            Remove
                          </Button>
                        </ImagePreview>
                      ) : (
                        <Upload
                          beforeUpload={handleHeroUpload}
                          accept="image/*"
                          showUploadList={false}
                          disabled={isUploadingHero}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={isUploadingHero}
                            disabled={isUploadingHero}
                          >
                            {isUploadingHero ? 'Uploading...' : 'Upload Hero Image'}
                          </Button>
                        </Upload>
                      )}
                      {/* Hero dimensions */}
                      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Hero Width
                          </Text>
                          <Field
                            name="hero_width"
                            label="Hero Width"
                            placeholder="600"
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                        <Col span={12}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Hero Height
                          </Text>
                          <Field
                            name="hero_height"
                            label="Hero Height"
                            placeholder="400"
                            component={TextInput}
                            type="number"
                          />
                        </Col>
                      </Row>
                    </ImageUploadSection>
                  </Col>
                </Row>
              </FormSection>

              <ButtonContainer>
                <Button onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSaving}
                  disabled={hasValidationErrors}
                >
                  {isEdit ? 'Update Ad' : 'Create Ad'}
                </Button>
              </ButtonContainer>
            </StyledForm>
          )
        }}
      />
    </Spin>
  )
}

AdForm.propTypes = {
  adId: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default AdForm